import { validate as uuidValidate } from "uuid";

import {
  ChangeEventAction,
  ChangeEventFamily,
  ChangeEventTimeline,
  ChangeEventTimelineCounts,
  ChangeEventTimelineEvent,
  ChangeEventTimelineScope,
  ChangeEventTimelineView,
} from "models";

/**
 * Everything on this page that is not JSX. Ported from the offline prototype
 * (api/scripts/e2e/change-events/statement/timeline.html) so the browser-side derivations — families,
 * summaries and filtering — stay readable in one place.
 */

export const FAMILIES: ChangeEventFamily[] = ["field", "crop", "points", "verification", "practice", "footprint", "money", "other"];

export const SCOPES: ChangeEventTimelineScope[] = ["field", "farm", "footprint"];

export const DIFF_PREVIEW = 8;
export const CHIP_PREVIEW = 8;
/** How many summarize() pairs are shown in the stronger headline style on a card. */
export const TOP_KV = 3;
const RAW_MAX = 200000;
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

export const DEFAULT_VIEW: ChangeEventTimelineView = {
  scope: SCOPES.slice(),
  fam: FAMILIES.slice(),
  q: "",
  sort: "desc",
  baseline: true,
  from: "",
  to: "",
};

/** label, value — the shape every card body is built from. */
export type SummaryPair = [string, string];

type ChipSpec = [string, string] | [string, string, number];

// ---------------------------------------------------------------- format

const numberFormats: Record<number, Intl.NumberFormat> = {};

const nf = (digits: number): Intl.NumberFormat => {
  if (!numberFormats[digits]) numberFormats[digits] = new Intl.NumberFormat("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
  return numberFormats[digits];
};

export const isNum = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

export const fmtNum = (value: unknown, digits = 2): string => (isNum(value) ? nf(digits).format(value) : "—");

export const fmtDelta = (value: unknown): string => {
  if (!isNum(value)) return "";
  if (Math.abs(value) < 0.005) return "±0.00";
  return (value > 0 ? "+" : "−") + fmtNum(Math.abs(value));
};

export const deltaDirection = (value: unknown): "up" | "down" | "" => {
  if (!isNum(value) || Math.abs(value) < 0.005) return "";
  return value > 0 ? "up" : "down";
};

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * Local time in the text, UTC in the tooltip: the prototype printed UTC and labelled it "Z", which
 * readers still misread as their own clock.
 */
export const fmtDate = (iso: string): { text: string; title: string } => {
  const date = new Date(iso);
  return {
    text: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`,
    title: `${iso} (UTC)`,
  };
};

export const fmtTime = (iso: string): string => {
  const date = new Date(iso);
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const fmtBytes = (bytes: unknown): string => {
  if (!isNum(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${fmtNum(bytes / 1024, 1)} KB`;
  return `${fmtNum(bytes / (1024 * 1024), 1)} MB`;
};

export const shortId = (id: string | null | undefined): string => (id ? String(id).slice(0, 8) : "—");

const dateOnly = (value: unknown): string | null => (value ? String(value).slice(0, 10) : null);

export const isUuid = (value: string): boolean => uuidValidate(value.trim());

/** Accepts a pasted log line or URL, not only a bare uuid. */
export const extractUuid = (value: string): string | null => {
  const match = UUID_RE.exec(value || "");
  return match ? match[0].toLowerCase() : null;
};

// ---------------------------------------------------------------- model

export const familyOf = (type: string): ChangeEventFamily => {
  if (type.indexOf("field.") === 0) return "field";
  if (type.indexOf("crop.") === 0) return "crop";
  if (type.indexOf("points.") === 0 || type.indexOf("pending-points.") === 0) return "points";
  if (type.indexOf("verification-") === 0) return "verification";
  if (type.indexOf("practice-participation.") === 0) return "practice";
  if (type.indexOf("footprint") === 0) return "footprint";
  if (type.indexOf("payout.") === 0 || type.indexOf("payment") === 0 || type.indexOf("credits.") === 0 || type.indexOf("credit-allotment.") === 0)
    return "money";
  return "other";
};

/** Written out rather than interpolated, so the stylesheet and the code name the same eight classes. */
const FAMILY_CLASS: Record<ChangeEventFamily, string> = {
  field: "fam-field",
  crop: "fam-crop",
  points: "fam-points",
  verification: "fam-verification",
  practice: "fam-practice",
  footprint: "fam-footprint",
  money: "fam-money",
  other: "fam-other",
};

export const familyClassName = (family: ChangeEventFamily): string => FAMILY_CLASS[family];

export const familyClass = (type: string): string => familyClassName(familyOf(type));

/** What the row did, read off the type's last segment — the one thing a reader wants before the type itself. */
export const actionOf = (type: string): ChangeEventAction => {
  const verb = type.slice(type.lastIndexOf(".") + 1);
  if (verb === "created" || verb === "added" || verb === "submitted") return "created";
  if (verb === "deleted" || verb === "removed" || verb === "rejected") return "deleted";
  if (verb === "calculated" || verb === "recalculated") return "calculated";
  if (verb === "updated" || verb === "changed" || verb === "approved") return "updated";
  return "other";
};

/** Written out rather than interpolated, so the stylesheet and the code name the same five classes. */
const ACTION_CLASS: Record<ChangeEventAction, string> = {
  created: "act-created",
  updated: "act-updated",
  deleted: "act-deleted",
  calculated: "act-calculated",
  other: "act-other",
};

const ACTION_ICON: Record<ChangeEventAction, string> = {
  created: "tabler/circle-plus",
  updated: "tabler/pencil",
  deleted: "tabler/trash",
  calculated: "tabler/refresh",
  other: "tabler/activity",
};

export const actionClass = (type: string): string => ACTION_CLASS[actionOf(type)];

export const actionIcon = (type: string): string => ACTION_ICON[actionOf(type)];

/** The pill above the type: "record deleted", or the bare verb when it is not a create/update/delete. */
export const actionLabel = (type: string): string => {
  const action = actionOf(type);
  if (action === "other") return type.slice(type.lastIndexOf(".") + 1).replace(/-/g, " ");
  if (action === "calculated") return "recalculated";
  return `record ${action}`;
};

const words = (value: string): string =>
  value
    .replace(/[_-]/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase();

/** Where the row came from, in the words an admin would use rather than the stored actorType. */
const sourceOf = (actorType: string): string => {
  if (actorType === "admin") return "the admin app";
  if (actorType === "user") return "the app";
  if (actorType === "system" || actorType === "cron" || actorType === "job") return "an automated job";
  if (actorType === "backfill") return "the backfill";
  return actorType ? words(actorType) : "an unknown source";
};

/** One plain sentence under the type, so a card reads without decoding the dotted name. */
export const describeEvent = (event: { type: string; entity: string; actorType: string; isBaseline: boolean }): string => {
  const subject = words(event.entity.replace(/^klim_/, ""));
  if (event.isBaseline) return `${subject} opening position, reconstructed by the backfill`;

  const verb = event.type.slice(event.type.lastIndexOf(".") + 1).replace(/-/g, " ");
  return `${subject} ${verb} via ${sourceOf(event.actorType)}`;
};

const FACT_ICONS: [RegExp, string][] = [
  [/year/i, "tabler/calendar"],
  [/(points|amount|payout|balance|price|credit)/i, "tabler/coin"],
  [/(practice|tillage|cover|crop|plant|seed|harvest)/i, "tabler/plant"],
  [/(field|farm|area|size|hectare)/i, "tabler/world"],
  [/(status|state|verification)/i, "tabler/check"],
  [/(table|entity|type|source|method)/i, "tabler/table"],
  [/(id|uuid|reference)/i, "tabler/hash"],
  [/(user|actor|author|owner)/i, "tabler/user"],
  [/(date|at$|time)/i, "tabler/clock"],
];

export const factIcon = (label: string): string => {
  const hit = FACT_ICONS.find(([pattern]) => pattern.test(label));
  return hit ? hit[1] : "tabler/database";
};

/** A year is an identifier, not a quantity: fmtNum would turn 2026 into "2,026.00". */
const isYearKey = (key: string): boolean => /year$/i.test(key);

const clampText = (value: unknown): string => {
  const text = String(value);
  return text.length > 60 ? `${text.slice(0, 57)}…` : text;
};

/** Chips for the keys actually present, so a partial snapshot never renders blank rows. */
export const chips = (source: Record<string, unknown> | null | undefined, specs: ChipSpec[]): SummaryPair[] => {
  if (!source) return [];

  return specs.reduce<SummaryPair[]>((out, spec) => {
    const value = source[spec[0]];
    if (value === undefined || value === null || value === "") return out;

    if (typeof value === "number" && isYearKey(spec[0])) out.push([spec[1], String(value)]);
    else if (typeof value === "number") out.push([spec[1], fmtNum(value, spec[2] === undefined ? 2 : spec[2])]);
    else if (typeof value === "boolean") out.push([spec[1], value ? "yes" : "no"]);
    else out.push([spec[1], clampText(value)]);

    return out;
  }, []);
};

/** Last resort so a newly added event type is never a blank card. */
export const fallbackChips = (input: Record<string, unknown> | null | undefined, limit: number): SummaryPair[] => {
  if (!input) return [];

  const keys = Object.keys(input);
  const out = keys.reduce<SummaryPair[]>((acc, key) => {
    if (acc.length >= limit) return acc;

    const value = input[key];
    if (value === null || value === undefined || value === "") return acc;
    if (Array.isArray(value)) {
      acc.push([key, `${value.length} item(s)`]);
      return acc;
    }
    if (typeof value === "object") return acc;

    if (typeof value === "number" && isYearKey(key)) acc.push([key, String(value)]);
    else if (typeof value === "number") acc.push([key, fmtNum(value)]);
    else if (typeof value === "boolean") acc.push([key, value ? "yes" : "no"]);
    else acc.push([key, clampText(value)]);

    return acc;
  }, []);

  if (keys.length > out.length) out.push(["keys", `${keys.length} in total`]);
  return out;
};

export const summarizePoints = (event: ChangeEventTimelineEvent): SummaryPair[] => {
  const { amount } = event;
  if (!amount) return [["result", "no points block for the selected year"]];

  const out: SummaryPair[] = [["points", fmtNum(amount.points)]];
  if (isNum(amount.payout)) out.push(["payout", `${fmtNum(amount.payout)} €`]);

  Object.keys(amount.bySlug || {}).forEach((slug) => {
    const per = amount.bySlug[slug];
    if (!per) return;
    out.push([
      slug,
      `rem ${fmtNum(per.removals)} · avo ${fmtNum(per.avoidances)} · red ${fmtNum(per.reductions)}  (range ${fmtNum(per.min)}–${fmtNum(per.max)})`,
    ]);
  });

  return out;
};

/**
 * The card body of one event. Every branch is ordered so the first three pairs — the ones promoted to
 * the headline — are the three facts worth reading first for that family.
 */
export const summarize = (event: ChangeEventTimelineEvent): SummaryPair[] => {
  const input = event.input || {};
  const family = familyOf(event.type);
  let out: SummaryPair[];

  if (family === "field") {
    out = chips(input, [
      ["name", "name"],
      ["size", "size (ha)"],
      ["soilPoints", "soil points", 0],
      ["soc", "SOC"],
      ["clay", "clay"],
      ["humusContent", "humus"],
      ["bulkDensity", "bulk density"],
      ["socConversionFactor", "SOC factor", 4],
    ]);
    const resulting = (event.metadata && event.metadata.resultingFieldIds) || input.resultingFieldIds;
    if (Array.isArray(resulting)) out.push(["split into", resulting.map((id) => shortId(String(id))).join(", ")]);
  } else if (family === "crop") {
    // The catalogue id sorts last on purpose: it is a uuid this page cannot resolve to a crop name,
    // so it must not occupy one of the three promoted slots.
    out = chips(
      {
        harvestStart: dateOnly(input.harvestStart),
        harvestEnd: dateOnly(input.harvestEnd),
        mainCrop: input.mainCrop,
        verificationStatus: input.verificationStatus,
      },
      [
        ["harvestStart", "harvest from"],
        ["harvestEnd", "harvest to"],
        ["verificationStatus", "verification"],
        ["mainCrop", "main crop"],
      ]
    );

    const childCounts: [string, string][] = [
      ["seedings", "seedings"],
      ["tillages", "tillages"],
      ["fertilizersMineral", "mineral fert."],
      ["fertilizersOrganic", "organic fert."],
      ["fertilizerManagement", "fert. management"],
    ];
    childCounts.forEach(([key, label]) => {
      const list = input[key];
      if (Array.isArray(list) && list.length) out.push([label, String(list.length)]);
    });

    if (input.cropId) out.push(["catalogue crop", shortId(String(input.cropId))]);
  } else if (family === "points") {
    out =
      event.type === "pending-points.deleted"
        ? chips(input, [
            ["year", "year"],
            ["reason", "reason"],
          ])
        : summarizePoints(event);
  } else if (family === "verification") {
    if (event.type === "verification-status.changed") {
      out = chips(input, [
        ["practiceType", "practice"],
        ["previousStatus", "from"],
        ["newStatus", "to"],
        ["year", "year"],
      ]);
    } else if (event.type === "verification-recalculation.triggered") {
      out = chips(input, [
        ["title", "title"],
        ["description", "description"],
      ]);
    } else {
      out = chips(input, [
        ["year", "year"],
        ["size", "size (ha)"],
        ["officialSize", "official size (ha)"],
        ["verified", "verified"],
      ]);
    }
  } else if (family === "practice") {
    out = chips(input, [
      ["practiceSlug", "practice"],
      ["startYear", "start year", 0],
      ["discontinuedYear", "discontinued", 0],
    ]);
    out.push(["table", event.entity.slice(-11) === "soil_plus_3" ? "SoilPlus3" : "standard"]);
  } else if (family === "footprint") {
    out = [["domain", event.entity.replace(/^footprinting_/, "").replace(/_primary_data$/, "")]];

    if (event.type === "footprint.calculated") {
      out.push(["engine version", String((event.metadata && event.metadata.version) || "—")]);
      out = out.concat(
        chips(event.outputScalars, [
          ["total", "total"],
          ["totalEmissions", "total emissions"],
          ["emissionsPerHa", "per ha"],
        ])
      );
    } else if (event.type.slice(-8) === ".deleted") {
      out.push(["snapshot", "null by contract — the previous row holds the last known state"]);
    } else {
      out = out.concat(
        chips(input, [
          ["totalFieldHectares", "hectares"],
          ["totalWeightTonsPerHa", "yield t/ha"],
          ["normalizedWeightTs", "normalised t"],
          ["soilPh", "soil pH"],
          ["electricityKwh", "electricity kWh", 0],
          ["dieselL", "diesel L", 0],
          ["renewablesPct", "renewables %"],
          ["year", "year", 0],
        ])
      );
    }
  } else if (family === "money") {
    // Amounts before years: on a payout row the number is the story and the year is context.
    out = chips(input, [
      ["payment", "amount"],
      ["errAllotment", "ERR allotment"],
      ["bonusAllotmentAmount", "bonus"],
      ["creditNote", "credit note"],
      ["year", "year", 0],
      ["verificationYear", "verification year", 0],
      ["reportingYear", "reporting year", 0],
      ["pointPriceRemovals", "€/point removals"],
      ["pointPriceReductions", "€/point reductions"],
      ["pointPriceAvoidances", "€/point avoidances"],
      ["practiceParticipationId", "participation"],
      ["reason", "reason"],
    ]);
  } else {
    out = chips(input, [
      ["totalAmount", "total amount"],
      ["amount", "amount"],
      ["practiceSlug", "practice"],
      ["setting", "setting"],
      ["programId", "program"],
      ["haLimit", "ha limit"],
      ["soc", "SOC"],
      ["referralValue", "referral value"],
      ["year", "year", 0],
      ["version", "version"],
    ]);
  }

  // A family branch that found nothing is exactly the "new event type" case the prototype rendered
  // blank, so fall through to the raw scalars rather than showing an empty card.
  if (!out.length) out = fallbackChips(input, 6);
  if (!out.length) out = [["entity", `${event.entity} ${shortId(event.entityId)}`]];
  return out;
};

/** Verification-state colours, so the page reads like the admin verification UI the team already uses. */
export const stateClassFor = (label: string, value: string): string | null => {
  if (["verification", "from", "to", "status"].indexOf(label) === -1) return null;

  const key = String(value).toLowerCase();
  if (key === "approved") return "state-approved";
  if (key === "auto_approved" || key === "auto-approved" || key === "autoapproved") return "state-auto-approved";
  if (key === "pending" || key === "in_review") return "state-pending";
  if (key === "rejected" || key === "declined") return "state-rejected";
  if (key === "unknown" || key === "null") return "state-unknown";
  return null;
};

// ---------------------------------------------------------------- filtering

const blobs = new WeakMap<ChangeEventTimelineEvent, string>();

/** Memoised per event: search runs over every row on every keystroke. */
export const searchBlob = (event: ChangeEventTimelineEvent): string => {
  const cached = blobs.get(event);
  if (cached !== undefined) return cached;

  const parts = [
    event.type,
    event.entity,
    event.entityId || "",
    event.actorType,
    event.actorName || "",
    event.actorRole || "",
    event.reason || "",
    event.scope,
    String(event.reportingYear || ""),
  ];

  (event.diff || []).forEach((row) => parts.push(row.key, row.from || "", row.to || ""));
  summarize(event).forEach((pair) => parts.push(pair[0], pair[1]));

  const blob = parts.join(" ").toLowerCase();
  blobs.set(event, blob);
  return blob;
};

export const matchesFilters = (event: ChangeEventTimelineEvent, view: ChangeEventTimelineView): boolean => {
  if (view.scope.indexOf(event.scope) === -1) return false;
  if (view.fam.indexOf(familyOf(event.type)) === -1) return false;
  if (!view.baseline && event.isBaseline) return false;
  if (view.from && event.createdAt.slice(0, 10) < view.from) return false;
  if (view.to && event.createdAt.slice(0, 10) > view.to) return false;
  if (view.q && searchBlob(event).indexOf(view.q.toLowerCase()) === -1) return false;
  return true;
};

/** Counts are computed pre-filter, so a chip never collapses to zero because it is switched off. */
export const countsFor = (events: ChangeEventTimelineEvent[]): ChangeEventTimelineCounts => {
  const byFamily = FAMILIES.reduce((acc, family) => ({ ...acc, [family]: 0 }), {} as Record<ChangeEventFamily, number>);
  const byScope = SCOPES.reduce((acc, scope) => ({ ...acc, [scope]: 0 }), {} as Record<ChangeEventTimelineScope, number>);
  let baseline = 0;

  events.forEach((event) => {
    byFamily[familyOf(event.type)] += 1;
    byScope[event.scope] += 1;
    if (event.isBaseline) baseline += 1;
  });

  return { byFamily: byFamily, byScope: byScope, baseline: baseline };
};

/** How many filter dimensions sit away from their default — the number on the filters button. */
export const activeFilterCount = (view: ChangeEventTimelineView): number => {
  let count = 0;
  if (view.scope.slice().sort().join(",") !== SCOPES.slice().sort().join(",")) count += 1;
  if (view.fam.slice().sort().join(",") !== FAMILIES.slice().sort().join(",")) count += 1;
  if (!view.baseline) count += 1;
  if (view.from) count += 1;
  if (view.to) count += 1;
  return count;
};

export const isDefaultView = (view: ChangeEventTimelineView): boolean => !activeFilterCount(view) && !view.q && view.sort === DEFAULT_VIEW.sort;

/** Toggle, or with shift: only this one — and shift again on the sole survivor puts everything back. */
export const toggleValue = <T>(list: T[], all: T[], value: T, solo: boolean): T[] => {
  if (solo) {
    const isOnlyOne = list.length === 1 && list[0] === value;
    return isOnlyOne ? all.slice() : [value];
  }
  return list.indexOf(value) === -1 ? list.concat([value]) : list.filter((item) => item !== value);
};

// ---------------------------------------------------------------- raw payloads

export const safeJson = (value: unknown): string => {
  let text: string | undefined;
  try {
    text = JSON.stringify(value, null, 2);
  } catch (error) {
    return `could not serialise: ${error instanceof Error ? error.message : String(error)}`;
  }

  if (text === undefined) return "null";
  if (text.length <= RAW_MAX) return text;
  return `${text.slice(0, RAW_MAX)}\n… truncated, ${text.length} chars in total`;
};

export const rawPayloadOf = (event: ChangeEventTimelineEvent): string =>
  safeJson({ input: event.input, output: event.output, outputScalars: event.outputScalars, metadata: event.metadata });

/** Local calendar day of an event, used for the date separators down the timeline. */
export const dayOf = (iso: string): string => fmtDate(iso).text.slice(0, 10);

/** The years the year selector offers: whatever the API found, plus the one it actually used. */
export const yearOptions = (data: ChangeEventTimeline): number[] => {
  const years = data.years.slice();
  if (years.indexOf(data.year) === -1) years.unshift(data.year);
  return years;
};
